import { usePermissions } from '@/modules/common';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';

import { WeekDayBreakdown, WeeklySummary } from '../../types/time-tracking';
import { WeekGridHeader } from './week-grid-header';
import { WeekProjectRow } from './week-project-row';
import { buildWeekSlots } from './week-slot';
import { WeekTimeOffStrip } from './week-timeoff-strip';
import { WeekTotalsRow } from './week-totals-row';

interface WeekGridProps {
  summary: WeeklySummary;
  dailyBreakdown: WeekDayBreakdown[];
  weekStart: Date;
}

/**
 * Top-level orchestrator for the Week-view grid.
 *
 * The grid is a single CSS grid (`.tt-week-grid`) where every section — the
 * header, each project row, the time-off strip, and the totals row — emits the
 * same nine cells: project label, seven day columns, weekly-total. Each
 * subsection lives in its own file so this component stays an assembler:
 *
 *   - `WeekGridHeader`   — top row with day-of-week chips + hover "+"
 *   - `WeekProjectRow`   — one row per assigned project (collapsible)
 *   - `WeekTimeOffStrip`     — time-off row with stacked per-day segments
 *   - `WeekTotalsRow`    — daily/weekly totals with basis tabs
 *
 * Below `lg` the whole grid scrolls horizontally; below `xl` the first
 * column shrinks so day cells stay readable on tablet without a scrollbar.
 */
export function WeekGrid({ summary, dailyBreakdown, weekStart }: WeekGridProps) {
  const slots = buildWeekSlots(weekStart, dailyBreakdown);
  const { hasPermission } = usePermissions();
  const canQuickAdd = hasPermission(OpsiumPolicy.WorklogsCreate);

  return (
    <div className="w-full min-w-0 max-w-full overflow-x-auto">
      <div className="tt-week-grid min-w-[860px] overflow-hidden rounded-lg border bg-card shadow-sm">
        <WeekGridHeader slots={slots} canQuickAdd={canQuickAdd} />
        {summary.projectSummaries.map(project => (
          <WeekProjectRow
            key={project.project.projectId}
            project={project}
            slots={slots}
            weekStart={weekStart}
          />
        ))}
        <WeekTimeOffStrip slots={slots} weekStart={weekStart} requests={summary.timeOffRequests} />
        <WeekTotalsRow slots={slots} summary={summary} />
      </div>
    </div>
  );
}
